import { useState, useEffect, useRef, useCallback } from 'react';
import { ReactSession } from '../utils/ReactSession.jsx';
import { AES, enc } from 'crypto-js';
import { NotificationContext } from '../contexts/NotificationContext.jsx';
import { campusToGuid, userToGuid } from '../libs.jsx';
import { Amplify } from '@aws-amplify/core';
import { generateClient } from '@aws-amplify/api';
import ApiRequester from './ApiRequester.jsx';

// Storage keys for active alarms in sessionStorage
const ACTIVE_ALARMS_STORAGE_KEY = 'activeAlarms';
const ACTIVE_CLOUD_ALARMS_STORAGE_KEY = 'activeCloudAlarms';

const DEVMODE = import.meta.env.VITE_DEVMODE === 'true';

// 4.0 schema subscription (CampusId: String!, PascalCase fields)
const SUBSCRIPTION_QUERY_40 = `
    subscription OnEmergencyAlert($CampusId: String!) {
        onEmergencyAlert(CampusId: $CampusId) {
            CampusId
            AlarmId
            EventDateTime
            AlertType
            AlertSubType
            Status
            DeviceId
            DeviceUse
            GroupIds
            SerialNumber
            LocationId
            LocationName
            LocationArea
            Near
            AcceptUsername
            AcceptDateTime
            Residents
            MacAddr
            AckDateTime
            Version
        }
    }
`;

// 5.0 schema subscription (campus_id: Int!) – full Alarm type per schema2
const SUBSCRIPTION_QUERY_50 = `
    subscription OnEmergencyAlert($campus_id: Int!) {
        onEmergencyAlert(campus_id: $campus_id) {
            campus_id
            event_id
            accepted_datetime_utc
            accepted_display
            accepted_username
            accepted_user_id
            accept_required
            ack_datetime_utc
            area_group_id
            area_group_name
            area_id
            area_name
            arrived_datetime_utc
            associated_event_id
            autocleartime
            autofinishtime
            device_description
            device_id
            device_name
            device_type_id
            device_use_id
            event_category
            event_category_id
            event_datetime_utc
            event_type
            event_type_id
            finish_datetime_utc
            finish_required
            group_ids
            latitude
            longitude
            radian
            manufacture_id
            manufacture_uuid
            near_area_id
            near_area_name
            residents {
                resident_first_name
                resident_last_name
                resident_name
                resident_id
            }
            version
        }
    }
`;

/**
 * Get AppSync API URL and key for 4.0 (is_cloud false) or 5.0 (is_cloud true).
 * @param {boolean} isCloud - true for 5.0 schema, false for 4.0
 * @returns {{ apiUrl: string, apiKey: string|undefined }}
 */
const getAppSyncConfig = (isCloud) => {
    if (isCloud) {
        return {
            apiUrl: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_50_API_URL : import.meta.env.VITE_APPSYNC_50_API_URL,
            apiKey: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_50_API_KEY : import.meta.env.VITE_APPSYNC_50_API_KEY
        };
    }
    return {
        apiUrl: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_API_URL : import.meta.env.VITE_APPSYNC_API_URL,
        apiKey: DEVMODE ? import.meta.env.VITE_DEV_APPSYNC_API_KEY : import.meta.env.VITE_APPSYNC_API_KEY
    };
};

/**
 * Derive status for 5.0 cloud alarms from accept/ack datetime fields.
 * @param {Object} alarm - 5.0 Alarm with accepted_datetime_utc, ack_datetime_utc (or accepted_display)
 * @returns {'Waiting'|'Accepted'|'Cleared'|'Arrived'}
 */
const deriveCloudAlarmStatus = (alarm) => {
    const hasAccept = (alarm?.accepted_datetime_utc != null && alarm.accepted_datetime_utc !== '') ||
        (alarm?.accepted_display != null && alarm.accepted_display !== '');
    const hasAck = alarm?.ack_datetime_utc != null && alarm.ack_datetime_utc !== '';
    if (!hasAccept && !hasAck) return 'Waiting';
    if (hasAccept && !hasAck) return 'Accepted';
    if (!hasAccept && hasAck) return 'Cleared';
    return 'Arrived';
};

/**
 * Derive Status from AcceptDateTime and AckDateTime (4.0 schema).
 * @param {Object} alarm - Alarm object with optional AcceptDateTime, AckDateTime
 * @returns {'Waiting'|'Accepted'|'Cleared'|'Arrived'}
 */
const deriveAlarmStatus = (alarm) => {
    const hasAccept = alarm?.AcceptDateTime != null && alarm.AcceptDateTime !== '';
    const hasAck = alarm?.AckDateTime != null && alarm.AckDateTime !== '';
    if (!hasAccept && !hasAck) return 'Waiting';
    if (hasAccept && !hasAck) return 'Accepted';
    if (!hasAccept && hasAck) return 'Cleared';
    return 'Arrived';
};

// Storage utility functions
const AlarmStorage = {
    /**
     * Get all active alarms from sessionStorage
     * @returns {Array} Array of active alarm objects
     */
    getActiveAlarms: () => {
        try {
            const stored = sessionStorage.getItem(ACTIVE_ALARMS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error reading active alarms from storage:', error);
            return [];
        }
    },

    /**
     * Save alarms array to sessionStorage
     * @param {Array} alarms - Array of alarm objects to store
     */
    saveActiveAlarms: (alarms) => {
        try {
            sessionStorage.setItem(ACTIVE_ALARMS_STORAGE_KEY, JSON.stringify(alarms));
        } catch (error) {
            console.error('Error saving active alarms to storage:', error);
        }
    },

    /**
     * Add or update an alarm in storage
     * @param {Object} alarm - Alarm object to add or update
     */
    addOrUpdateAlarm: (alarm) => {
        if (!alarm || !alarm.AlarmId) {
            console.warn('Invalid alarm data, cannot store:', alarm);
            return;
        }

        const alarms = AlarmStorage.getActiveAlarms();
        const existingIndex = alarms.findIndex(a => a.AlarmId === alarm.AlarmId);
        const mergedAlarm = existingIndex >= 0
            ? { ...alarms[existingIndex], ...alarm }
            : { ...alarm };
        mergedAlarm.Status = deriveAlarmStatus(mergedAlarm);
        // Parse Residents string into ResidentsObject for front-end use
        try {
            const residentsRaw = mergedAlarm.Residents;
            mergedAlarm.ResidentsObject = residentsRaw != null && residentsRaw !== ''
                ? (typeof residentsRaw === 'string' ? JSON.parse(residentsRaw) : residentsRaw)
                : null;
        } catch (_) {
            mergedAlarm.ResidentsObject = null;
        }
        // Parse Near string (JSON array) into NearObject and derive NearName from each record's name
        try {
            const nearRaw = mergedAlarm.Near;
            const nearArr = nearRaw != null && nearRaw !== ''
                ? (typeof nearRaw === 'string' ? JSON.parse(nearRaw) : nearRaw)
                : null;
            mergedAlarm.NearObject = Array.isArray(nearArr) ? nearArr : (nearArr ? [nearArr] : null);
            mergedAlarm.NearName = mergedAlarm.NearObject?.length
                ? mergedAlarm.NearObject.map((r) => r?.name).filter(Boolean).join(', ') || ''
                : '';
        } catch (_) {
            mergedAlarm.NearObject = null;
            mergedAlarm.NearName = '';
        }

        if (existingIndex >= 0) {
            alarms[existingIndex] = mergedAlarm;
        } else {
            alarms.push(mergedAlarm);
        }

        AlarmStorage.saveActiveAlarms(alarms);
    },

    /**
     * Remove an alarm from storage by AlarmId
     * @param {string} alarmId - AlarmId to remove
     */
    removeAlarm: (alarmId) => {
        const alarms = AlarmStorage.getActiveAlarms();
        const filtered = alarms.filter(a => a.AlarmId !== alarmId);
        AlarmStorage.saveActiveAlarms(filtered);
    },

    /**
     * Clear all alarms from storage
     */
    clearAllAlarms: () => {
        try {
            sessionStorage.removeItem(ACTIVE_ALARMS_STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing active alarms from storage:', error);
        }
    },

    /**
     * Get a specific alarm by AlarmId
     * @param {string} alarmId - AlarmId to retrieve
     * @returns {Object|null} Alarm object or null if not found
     */
    getAlarmById: (alarmId) => {
        const alarms = AlarmStorage.getActiveAlarms();
        return alarms.find(a => a.AlarmId === alarmId) || null;
    }
};

// 5.0 cloud alarm storage (native snake_case schema; for CurrentCloudAlerts / DemoPanelCloud)
const CloudAlarmStorage = {
    getActiveAlarms: () => {
        try {
            const stored = sessionStorage.getItem(ACTIVE_CLOUD_ALARMS_STORAGE_KEY);
            return stored ? JSON.parse(stored) : [];
        } catch (error) {
            console.error('Error reading active cloud alarms from storage:', error);
            return [];
        }
    },

    saveActiveAlarms: (alarms) => {
        try {
            sessionStorage.setItem(ACTIVE_CLOUD_ALARMS_STORAGE_KEY, JSON.stringify(alarms));
        } catch (error) {
            console.error('Error saving active cloud alarms to storage:', error);
        }
    },

    addOrUpdateAlarm: (alarm) => {
        if (!alarm || alarm.event_id == null) {
            console.warn('Invalid cloud alarm data, cannot store:', alarm);
            return;
        }
        const alarms = CloudAlarmStorage.getActiveAlarms();
        const existingIndex = alarms.findIndex(a => a.event_id === alarm.event_id);
        const merged = existingIndex >= 0 ? { ...alarms[existingIndex], ...alarm } : { ...alarm };
        merged.status = deriveCloudAlarmStatus(merged);
        if (existingIndex >= 0) alarms[existingIndex] = merged;
        else alarms.push(merged);
        CloudAlarmStorage.saveActiveAlarms(alarms);
    },

    removeAlarm: (eventId) => {
        const alarms = CloudAlarmStorage.getActiveAlarms().filter(a => a.event_id !== eventId);
        CloudAlarmStorage.saveActiveAlarms(alarms);
    },

    clearAllAlarms: () => {
        try {
            sessionStorage.removeItem(ACTIVE_CLOUD_ALARMS_STORAGE_KEY);
        } catch (error) {
            console.error('Error clearing active cloud alarms from storage:', error);
        }
    },

    getAlarmByEventId: (eventId) => {
        const alarms = CloudAlarmStorage.getActiveAlarms();
        return alarms.find(a => a.event_id === eventId) || null;
    }
};

const AlertNotificationProvider = ({ children }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [activeAlarms, setActiveAlarms] = useState([]);
    const [activeCloudAlarms, setActiveCloudAlarms] = useState([]);
    const subscriptionRef = useRef(null);
    const lastConfiguredIsCloud = useRef(null);
    const clientRef = useRef(null);
    const initialLoadDone = useRef(false);

    // Load active alarms from storage on mount (4.0 and 5.0 separate)
    useEffect(() => {
        setActiveAlarms(AlarmStorage.getActiveAlarms());
        setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
    }, []);

    /**
     * Configure Amplify for 4.0 or 5.0 AppSync based on is_cloud. Reconfigures when is_cloud changes.
     * @param {boolean} isCloud - true for 5.0 schema, false for 4.0
     * @returns {boolean} true if configured successfully
     */
    const ensureAmplifyConfigured = useCallback((isCloud) => {
        if (lastConfiguredIsCloud.current === isCloud && clientRef.current) {
            return true;
        }
        try {
            const { apiUrl, apiKey } = getAppSyncConfig(isCloud);
            const region = import.meta.env.VITE_APPSYNC_REGION;

            if (!apiUrl) {
                const msg = isCloud ? 'VITE_*_APPSYNC_50_API_URL not configured' : 'VITE_APPSYNC_API_URL not configured';
                console.warn(msg);
                setConnectionError('AppSync API URL not configured');
                return false;
            }

            const amplifyConfig = {
                API: {
                    GraphQL: {
                        endpoint: apiUrl,
                        region: region,
                        defaultAuthMode: apiKey ? 'apiKey' : 'lambda',
                        ...(apiKey && { apiKey })
                    }
                }
            };

            Amplify.configure(amplifyConfig);
            clientRef.current = generateClient();
            lastConfiguredIsCloud.current = isCloud;
            // console.log('Amplify configured for AppSync:', {schema: isCloud ? '5.0' : '4.0', endpoint: apiUrl, region, authType: apiKey ? 'API_KEY' : 'AWS_LAMBDA'});
            return true;
        } catch (error) {
            console.error('Error configuring Amplify:', error);
            setConnectionError(`Configuration error: ${error.message}`);
            return false;
        }
    }, []);

    // Get session data
    const getSessionData = () => {
        try {
            if (ReactSession.get("PAL") != null) {
                const bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
                return JSON.parse(bData.toString(enc.Utf8));
            }
        } catch (error) {
            console.error('Error getting session data:', error);
        }
        return null;
    };

    // Get authentication token from session
    const getAuthToken = () => {
        const dData = getSessionData();
        return dData?.tKey || null;
    };

    // Load initial active alarms from DynamoDB
    const loadInitialAlarms = useCallback(async (campusGuid) => {
        try {
            const dData = getSessionData();
            if (!dData) {
                console.warn('No session data available for loading initial alarms');
                return;
            }

            // If campusGuid is not provided, get it from session
            const finalCampusGuid = campusGuid || campusToGuid(dData, dData.acnt);
            const userId = userToGuid(dData, dData.user);

            // console.log('Loading initial active alarms from DynamoDB...', {campusGuid: finalCampusGuid, userId});

            // Load emergency alarms
            let emergAlarms;
            if (dData?.campusData[dData?.acnt.toString()]?.is_cloud) {
                emergAlarms = await ApiRequester({
                    reqEndpoint: "get-alerts-cloud",
                    CampusId: dData.acnt,
                    ToGet: "emergency",
                    UserId: dData.user,
                    Mode: "standard"
                });
            } else {
                emergAlarms = await ApiRequester({
                    reqEndpoint: "get-alerts",
                    CampusId: finalCampusGuid,
                    mode: "standard",
                    hybrid: dData?.hybrid,
                    legacy: dData?.legacy,
                    new: dData?.new,
                    ToGet: "alarm",
                    UserId: userId,
                    Migrated: dData.migrated
                });
            }

            // // Load maintenance alarms
            // const maintAlarms = await ApiRequester({
            //     reqEndpoint: "get-alerts",
            //     CampusId: finalCampusGuid,
            //     mode: "standard",
            //     hybrid: dData?.hybrid,
            //     legacy: dData?.legacy,
            //     new: dData?.new,
            //     ToGet: "maint",
            //     UserId: userId,
            //     Migrated: dData.migrated
            // });

            const allAlarms = [...(emergAlarms || [])];
            const isCloud = !!dData?.campusData[dData?.acnt.toString()]?.is_cloud;

            if (isCloud) {
                AlarmStorage.clearAllAlarms();
                setActiveAlarms([]);
                const toStore = allAlarms.filter(
                    a => a && a.event_id != null && a.event_datetime_utc != null && a.event_datetime_utc !== ''
                );
                CloudAlarmStorage.clearAllAlarms();
                toStore.forEach(alarm => CloudAlarmStorage.addOrUpdateAlarm(alarm));
                setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
                // console.log(`Loaded ${toStore.length} initial cloud (5.0) alarms from DynamoDB`);
                // console.log(toStore)
            } else {
                CloudAlarmStorage.clearAllAlarms();
                setActiveCloudAlarms([]);
                const toStore = allAlarms.filter(
                    alarm => alarm && alarm.AlarmId && alarm.EventDateTime != null && alarm.EventDateTime !== ''
                );
                AlarmStorage.clearAllAlarms();
                if (toStore.length > 0) {
                    toStore.forEach(alarm => AlarmStorage.addOrUpdateAlarm(alarm));
                    setActiveAlarms(AlarmStorage.getActiveAlarms());
                    // console.log(`Loaded ${toStore.length} initial (4.0) alarms from DynamoDB`);
                } else {
                    setActiveAlarms([]);
                }
            }
        } catch (error) {
            console.error('Error loading initial alarms from DynamoDB:', error);
        }
    }, []);

    // Subscribe to emergency alerts using Amplify (4.0 or 5.0 based on dData.is_cloud)
    const subscribeToEmergencyAlerts = useCallback((campusGuid) => {
        initialLoadDone.current = false;

        const dData = getSessionData();
        if (!dData?.acnt) {
            console.warn('No session data or campus for subscription');
            return null;
        }

        const isCloud = !!dData?.campusData[dData?.acnt.toString()]?.is_cloud;
        if (!ensureAmplifyConfigured(isCloud)) {
            return null;
        }

        // Unsubscribe from existing subscription if any
        if (subscriptionRef.current) {
            subscriptionRef.current.unsubscribe();
            subscriptionRef.current = null;
        }

        const { apiKey } = getAppSyncConfig(isCloud);
        const subscriptionQuery = isCloud ? SUBSCRIPTION_QUERY_50 : SUBSCRIPTION_QUERY_40;
        const campusSubId = isCloud ? Number(dData.acnt) : campusGuid;
        const variables = isCloud ? { campus_id: campusSubId } : { CampusId: campusSubId };

        try {
            if (!clientRef.current) {
                console.error('GraphQL client not initialized');
                setConnectionError('GraphQL client not initialized');
                return null;
            }

            // console.log('Subscribing to emergency alerts with Amplify:', {
            //     schema: isCloud ? '5.0' : '4.0',
            //     campusSubId,
            //     hasApiKey: !!apiKey
            // });

            const subscription = clientRef.current.graphql({
                query: subscriptionQuery,
                variables,
                authMode: apiKey ? 'apiKey' : 'lambda'
            }).subscribe({
                next: (data) => {
                    const raw = data.value?.data?.onEmergencyAlert || data.data?.onEmergencyAlert || data;
                    // console.log('Received AppSync subscription data:', raw);

                    if (isCloud) {
                        // 5.0: store native schema; remove if no event_datetime_utc
                        if (raw && raw.event_id != null) {
                            const hasEventDateTime = (raw.event_datetime_utc ?? '') !== '';
                            if (!hasEventDateTime) {
                                CloudAlarmStorage.removeAlarm(raw.event_id);
                            } else {
                                CloudAlarmStorage.addOrUpdateAlarm(raw);
                            }
                            setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
                        }
                    } else {
                        // 4.0: store native schema
                        if (raw && raw.AlarmId) {
                            const hasEventDateTime = (raw.EventDateTime ?? '') !== '';
                            if (!hasEventDateTime) {
                                AlarmStorage.removeAlarm(raw.AlarmId);
                            } else {
                                AlarmStorage.addOrUpdateAlarm(raw);
                            }
                            setActiveAlarms(AlarmStorage.getActiveAlarms());
                        }
                    }

                    setIsConnected(true);
                    setConnectionError(null);
                },
                error: (error) => {
                    console.error('Subscription error:', error);
                    setConnectionError(error.message || 'Subscription error');
                    setIsConnected(false);
                }
            });

            subscriptionRef.current = subscription;
            setIsConnected(true);
            setConnectionError(null);
            console.log('Successfully subscribed to emergency alerts');

            if (!initialLoadDone.current) {
                initialLoadDone.current = true;
                loadInitialAlarms(isCloud ? undefined : campusGuid).catch(error => {
                    console.error('Error during initial alarm load:', error);
                });
            }

            return subscription;
        } catch (error) {
            console.error('Error creating subscription:', error);
            setConnectionError(`Subscription error: ${error.message}`);
            setIsConnected(false);
            return null;
        }
    }, [loadInitialAlarms, ensureAmplifyConfigured]);

    // Unsubscribe from subscription
    const unsubscribe = useCallback(() => {
        if (subscriptionRef.current) {
            try {
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
                setIsConnected(false);
                console.log('Unsubscribed from emergency alerts');
            } catch (error) {
                console.error('Error unsubscribing:', error);
            }
        }
    }, []);

    // Auto-subscribe to emergency alerts when we have session data
    useEffect(() => {
        if (ReactSession.get("PAL") != null) {
            try {
                const bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
                const dData = JSON.parse(bData.toString(enc.Utf8));

                if (dData?.acnt) {
                    const campusGuid = campusToGuid(dData, dData.acnt);
                    subscribeToEmergencyAlerts(campusGuid);
                }
            } catch (error) {
                console.error('Error setting up subscription:', error);
            }
        }

        return () => {
            unsubscribe();
        };
    }, [subscribeToEmergencyAlerts, unsubscribe]);

    // Reconnect when session changes (e.g. campus switch from CampusDropdown)
    useEffect(() => {
        const checkSession = () => {
            const hasSession = ReactSession.get("PAL") != null;

            if (hasSession && !subscriptionRef.current) {
                try {
                    const bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
                    const dData = JSON.parse(bData.toString(enc.Utf8));

                    if (dData?.acnt) {
                        const campusGuid = campusToGuid(dData, dData.acnt);
                        subscribeToEmergencyAlerts(campusGuid);
                    }
                } catch (error) {
                    console.error('Error reconnecting subscription:', error);
                }
            } else if (!hasSession && subscriptionRef.current) {
                unsubscribe();
            }
        };

        const interval = setInterval(checkSession, 5000);
        return () => clearInterval(interval);
    }, [subscribeToEmergencyAlerts, unsubscribe]);

    // Resubscribe to current campus from session (e.g. CampusDropdown)
    const resubscribeToCurrentCampus = useCallback(() => {
        try {
            if (ReactSession.get("PAL") != null) {
                const bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
                const dData = JSON.parse(bData.toString(enc.Utf8));

                if (dData?.acnt) {
                    const campusGuid = campusToGuid(dData, dData.acnt);
                    console.log('Resubscribing to new campus:', campusGuid);
                    subscribeToEmergencyAlerts(campusGuid);
                }
            }
        } catch (error) {
            console.error('Error resubscribing to current campus:', error);
        }
    }, [subscribeToEmergencyAlerts]);

    const value = {
        isConnected,
        connectionError,
        subscribeToEmergencyAlerts,
        unsubscribe,
        resubscribeToCurrentCampus,
        reconnect: () => {
            unsubscribe();
            if (ReactSession.get("PAL") != null) {
                try {
                    const bData = AES.decrypt(ReactSession.get("PAL"), import.meta.env.VITE_ESECRET);
                    const dData = JSON.parse(bData.toString(enc.Utf8));
                    if (dData?.acnt) {
                        const campusGuid = campusToGuid(dData, dData.acnt);
                        subscribeToEmergencyAlerts(campusGuid);
                    }
                } catch (error) {
                    console.error('Error reconnecting:', error);
                }
            }
        },
        // 4.0 active alarms (CurrentAlertsV2, DemoPanel)
        activeAlarms,
        getActiveAlarms: () => AlarmStorage.getActiveAlarms(),
        getAlarmById: (alarmId) => AlarmStorage.getAlarmById(alarmId),
        removeAlarm: (alarmId) => {
            AlarmStorage.removeAlarm(alarmId);
            setActiveAlarms(AlarmStorage.getActiveAlarms());
        },
        clearAllAlarms: () => {
            AlarmStorage.clearAllAlarms();
            setActiveAlarms([]);
        },
        refreshAlarms: () => {
            setActiveAlarms(AlarmStorage.getActiveAlarms());
        },
        // 5.0 cloud active alarms (CurrentCloudAlerts emergData, DemoPanelCloud)
        activeCloudAlarms,
        getActiveCloudAlarms: () => CloudAlarmStorage.getActiveAlarms(),
        getCloudAlarmByEventId: (eventId) => CloudAlarmStorage.getAlarmByEventId(eventId),
        removeCloudAlarm: (eventId) => {
            CloudAlarmStorage.removeAlarm(eventId);
            setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
        },
        clearAllCloudAlarms: () => {
            CloudAlarmStorage.clearAllAlarms();
            setActiveCloudAlarms([]);
        },
        refreshCloudAlarms: () => {
            setActiveCloudAlarms(CloudAlarmStorage.getActiveAlarms());
        }
    };

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export default AlertNotificationProvider;
